// Transport Abstraction - WebSocket only
// This module provides a unified interface for communication with the backend

import { WebSocketTransport } from './websocket';
import type {
  CoworkSession,
  CoworkSessionSummary,
  CoworkConfig,
  CoworkConfigUpdate,
  CoworkPermissionRequest,
  CoworkPermissionResult,
  CoworkStartOptions,
  CoworkContinueOptions,
  CoworkUserMemoryEntry,
  CoworkMemoryStats,
  CoworkSandboxStatus,
  CoworkSandboxProgress,
  CoworkApiConfig,
} from '../types/cowork';

export type StreamEventType = 'message' | 'messageUpdate' | 'permissionRequest' | 'complete' | 'error' | 'scheduledTask:statusUpdate' | 'scheduledTask:runUpdate';

export interface StreamEventPayloads {
  message: { sessionId: string; message: CoworkSession['messages'][0] };
  messageUpdate: { sessionId: string; messageId: string; content: string };
  permissionRequest: { sessionId: string; request: CoworkPermissionRequest };
  complete: { sessionId: string; claudeSessionId: string | null };
  error: { sessionId: string; error: string };
  'scheduledTask:statusUpdate': unknown;
  'scheduledTask:runUpdate': unknown;
}

export interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ConnectionStatus {
  connected: boolean;
  type: 'websocket';
  serverUrl?: string;
  error?: string;
  authenticated?: boolean;
  userId?: string;
}

type StreamListener<K extends StreamEventType> = (payload: StreamEventPayloads[K]) => void;

export interface TransportConfig {
  type?: 'websocket';
  serverUrl?: string;
  token?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export interface Transport {
  getConnectionStatus(): ConnectionStatus;
  onStatusChange?(listener: (status: ConnectionStatus) => void): () => void;
  setToken?(token: string | null): void;
  connect?(): Promise<void>;
  disconnect(): void;

  startSession(options: CoworkStartOptions): Promise<ApiResult<{ session: CoworkSession }>>;
  continueSession(options: CoworkContinueOptions): Promise<ApiResult<unknown>>;
  stopSession(sessionId: string): Promise<ApiResult<unknown>>;
  getSession(sessionId: string): Promise<ApiResult<{ session: CoworkSession | null }>>;
  listSessions(): Promise<ApiResult<{ sessions: CoworkSessionSummary[] }>>;
  deleteSession(sessionId: string): Promise<ApiResult<unknown>>;
  deleteSessions(sessionIds: string[]): Promise<ApiResult<unknown>>;
  setSessionPinned(sessionId: string, pinned: boolean): Promise<ApiResult<unknown>>;
  renameSession(sessionId: string, title: string): Promise<ApiResult<unknown>>;

  getConfig(): Promise<ApiResult<{ config: CoworkConfig }>>;
  setConfig(config: CoworkConfigUpdate): Promise<ApiResult<unknown>>;

  respondToPermission(requestId: string, result: CoworkPermissionResult): Promise<ApiResult<unknown>>;

  listMemoryEntries(input: {
    query?: string;
    status?: 'created' | 'stale' | 'deleted' | 'all';
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResult<{ entries: CoworkUserMemoryEntry[] }>>;
  createMemoryEntry(input: {
    text: string;
    confidence?: number;
    isExplicit?: boolean;
  }): Promise<ApiResult<{ entry: CoworkUserMemoryEntry }>>;
  updateMemoryEntry(input: {
    id: string;
    text?: string;
    confidence?: number;
    status?: 'created' | 'stale' | 'deleted';
    isExplicit?: boolean;
  }): Promise<ApiResult<{ entry: CoworkUserMemoryEntry }>>;
  deleteMemoryEntry(input: { id: string }): Promise<ApiResult<unknown>>;
  getMemoryStats(): Promise<ApiResult<{ stats: CoworkMemoryStats }>>;

  getSandboxStatus(): Promise<CoworkSandboxStatus | null>;
  installSandbox(): Promise<{ success: boolean; status: CoworkSandboxStatus; error?: string } | null>;
  onSandboxDownloadProgress(callback: (progress: CoworkSandboxProgress) => void): () => void;

  getApiConfig(): Promise<CoworkApiConfig | null>;
  checkApiConfig(options?: { probeModel?: boolean }): Promise<{ hasConfig: boolean; config: CoworkApiConfig | null; error?: string } | null>;
  saveApiConfig(config: CoworkApiConfig): Promise<{ success: boolean; error?: string } | null>;

  storeGet<T = unknown>(key: string): Promise<T | undefined>;
  storeSet<T = unknown>(key: string, value: T): Promise<void>;
  storeDelete(key: string): Promise<void>;

  generateSessionTitle(prompt: string | null): Promise<string | null>;
  getRecentCwds(limit?: number): Promise<string[]>;

  getGmailStatus?(): Promise<{ success: boolean; connected?: boolean; email?: string; error?: string; authUrl?: string }>;
  getGmailAuthUrl?(): Promise<{ success: boolean; url?: string; error?: string }>;
  disconnectGmail?(): Promise<{ success: boolean; message?: string; error?: string }>;
  testGmailConnection?(): Promise<{ success: boolean; email?: string; error?: string }>;
  getGmailWatcherFilter?(): Promise<{ success: boolean; filter?: Record<string, unknown>; error?: string }>;
  setGmailWatcherFilter?(filter: Record<string, unknown>): Promise<{ success: boolean; filter?: Record<string, unknown>; error?: string }>;
  listGmailMessages?(query?: string, maxResults?: number, pageToken?: string): Promise<{
    success: boolean;
    messages?: Array<{ id: string; threadId: string }>;
    resultSizeEstimate?: number;
    nextPageToken?: string;
    error?: string;
  }>;
  getGmailMessage?(messageId: string): Promise<{ success: boolean; message?: unknown; error?: string }>;
  sendGmailMessage?(params: {
    to: string; subject: string; body: string; cc?: string; bcc?: string;
    replyToMessageId?: string; html?: boolean;
  }): Promise<{ success: boolean; id?: string; threadId?: string; error?: string }>;
  listGmailLabels?(): Promise<{ success: boolean; labels?: Array<{ id: string; name: string; type: string }>; error?: string }>;
  modifyGmailMessage?(messageId: string, addLabels?: string[], removeLabels?: string[]): Promise<{ success: boolean; id?: string; labelIds?: string[]; error?: string }>;
  deleteGmailMessage?(messageId: string): Promise<{ success: boolean; error?: string }>;
  getGmailAttachment?(messageId: string, attachmentId: string): Promise<{ success: boolean; data?: string; size?: number; error?: string }>;

  listLogs?(query?: {
    userId?: string;
    category?: string;
    status?: string;
    subjectId?: string;
    startTimeFrom?: number;
    startTimeTo?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; logs?: import('../types/log').Log[]; error?: string }>;
  getLog?(id: string): Promise<{ success: boolean; log?: import('../types/log').Log; error?: string }>;
  createLog?(input: import('../types/log').CreateLogInput): Promise<{ success: boolean; log?: import('../types/log').Log; error?: string }>;
  updateLog?(id: string, input: import('../types/log').UpdateLogInput): Promise<{ success: boolean; log?: import('../types/log').Log; error?: string }>;
  deleteLog?(id: string): Promise<{ success: boolean; error?: string }>;
  getLogSubjects?(logId: string): Promise<{ success: boolean; subjects?: import('../types/log').Subject[]; error?: string }>;
  addSubjectToLog?(logId: string, subjectId: string): Promise<{ success: boolean; result?: boolean; error?: string }>;
  removeSubjectFromLog?(logId: string, subjectId: string): Promise<{ success: boolean; result?: boolean; error?: string }>;

  listSubjects?(userId?: string): Promise<{ success: boolean; subjects?: import('../types/log').Subject[]; error?: string }>;
  getSubject?(id: string): Promise<{ success: boolean; subject?: import('../types/log').Subject; error?: string }>;
  createSubject?(input: import('../types/log').CreateSubjectInput): Promise<{ success: boolean; subject?: import('../types/log').Subject; error?: string }>;
  updateSubject?(id: string, input: import('../types/log').UpdateSubjectInput): Promise<{ success: boolean; subject?: import('../types/log').Subject; error?: string }>;
  deleteSubject?(id: string): Promise<{ success: boolean; error?: string }>;
  getSubjectLogs?(subjectId: string): Promise<{ success: boolean; logs?: import('../types/log').Log[]; error?: string }>;

  on<K extends StreamEventType>(event: K, listener: StreamListener<K>): () => void;
  off<K extends StreamEventType>(event: K, listener: StreamListener<K>): void;
  onEvent(event: string, listener: (payload: unknown) => void): () => void;
  destroy(): void;
}

let transportInstance: Transport | null = null;

export function getTransport(config?: TransportConfig): Transport {
  if (transportInstance) {
    return transportInstance;
  }

  const serverUrl = config?.serverUrl;
  if (!serverUrl) {
    throw new Error('WebSocket transport requires serverUrl');
  }

  transportInstance = new WebSocketTransport({
    type: 'websocket',
    serverUrl,
    token: config?.token,
    reconnectInterval: config?.reconnectInterval,
    maxReconnectAttempts: config?.maxReconnectAttempts,
  }) as Transport;
  console.log('[Transport] Using WebSocket transport');
  return transportInstance;
}

export function resetTransport(): void {
  if (transportInstance) {
    transportInstance.destroy();
    transportInstance = null;
  }
}

export function createWebSocketTransport(config: {
  serverUrl: string;
  token?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}): Transport {
  return new WebSocketTransport({
    type: 'websocket',
    ...config,
  });
}

export { WebSocketTransport } from './websocket';